import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { quoteRegions, regionLabelFromParam } from "../data/quoteRegions";
import { allServices } from "../data/services";
import { stateOutlines } from "../components/OurLocations";

const outlineById = Object.fromEntries(stateOutlines.map((state) => [state.id, state.d]));

const services = [...allServices.map((service) => service.label), "Other"];

const inputClass = (err) =>
  `w-full rounded-xl border px-4 py-3 text-sm text-black placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
    err ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
  }`;

const labelClass = "block mb-1.5 text-sm font-medium text-gray-700";

function StateIcon({ id, className = "h-4 w-4" }) {
  const d = outlineById[id];
  if (!d) return null;
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden>
      <path d={d} fill="currentColor" />
    </svg>
  );
}

function RegionSelect({ value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = quoteRegions.find((region) => region.label === value);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={`${inputClass(error)} flex items-center gap-2 text-left`}
      >
        {selected ? (
          <>
            <StateIcon id={selected.id} className="h-4 w-4 shrink-0 text-orange-500" />
            <span className="min-w-0 flex-1 truncate">{selected.label}</span>
          </>
        ) : (
          <span className="min-w-0 flex-1 truncate text-gray-400">Where do you need the plan?</span>
        )}
        <svg
          className={`h-4 w-4 shrink-0 text-gray-400 transition ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Region"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          {quoteRegions.map((region) => {
            const active = region.label === value;
            return (
              <li key={region.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(region.label);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm ${
                    active ? "bg-orange-50 font-semibold text-orange-600" : "text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <StateIcon id={region.id} className="h-4 w-4 shrink-0 text-orange-500" />
                  {region.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className={labelClass}>
        {label}{required && <span className="ml-0.5 text-orange-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function InfoRow({ icon, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-500">
        {icon}
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-gray-500">{title}</p>
        <div className="mt-0.5 text-sm text-gray-800">{children}</div>
      </div>
    </div>
  );
}

/** Same-origin on Vercel (www.focustrafficplans.com.au/api/contact). Override only if the API is hosted elsewhere. */
function contactSubmitUrl() {
  const base = (process.env.REACT_APP_CONTACT_API_URL || "").replace(/\/$/, "");
  return base ? `${base}/api/contact` : "/api/contact";
}

function emptyForm(region = "") {
  return {
    firstName: "", lastName: "", email: "", phone: "",
    company: "", region, service: "", details: "", startDate: "",
  };
}

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() =>
    emptyForm(regionLabelFromParam(searchParams.get("region")))
  );
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitMeta, setSubmitMeta] = useState({
    confirmationSent: true,
    serverMessage: "",
  });

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setErrors((p) => ({ ...p, [e.target.name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim())  e.lastName  = "Required";
    if (!form.email.trim())     e.email     = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim())     e.phone     = "Required";
    if (!form.region)           e.region    = "Please select a region";
    if (!form.service)          e.service   = "Please select a service";
    if (!form.details.trim())   e.details   = "Required";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitError("");
    setSubmitting(true);
    try {
      const res = await fetch(contactSubmitUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        /* non-JSON body */
      }
      if (!res.ok) {
        setSubmitError(
          data.error ||
            "We could not send your enquiry. Please try again or call 1300 118 170."
        );
        return;
      }
      setSubmitMeta({
        confirmationSent: data.confirmationSent !== false,
        serverMessage: typeof data.message === "string" ? data.message : "",
      });
      setSubmitted(true);
    } catch {
      setSubmitError(
        "Network error. Check your connection or call 1300 118 170."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-100 border-t-4 border-t-orange-500 bg-white p-10 text-center shadow-xl">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-orange-50">
            <svg className="h-8 w-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mb-2 text-2xl font-bold text-black">Enquiry Submitted!</h2>
          <p className="mb-6 text-left text-sm leading-relaxed text-gray-600">
            Thanks, {form.firstName}. We&apos;ll get back to you within{" "}
            <strong className="font-semibold text-gray-800">12 hours</strong>.
            {submitMeta.confirmationSent ? (
              <>
                {" "}
                We&apos;ve sent a confirmation email to{" "}
                <strong className="font-semibold text-gray-800">{form.email}</strong>
                — please check your inbox (and spam folder).
              </>
            ) : (
              <>
                {" "}
                {submitMeta.serverMessage ||
                  "We couldn’t send a confirmation email, but your enquiry is with our team — we’ll still reply within 12 hours."}
              </>
            )}
          </p>
          <button
            onClick={() => {
              setForm(emptyForm());
              setSubmitMeta({ confirmationSent: true, serverMessage: "" });
              setSubmitted(false);
            }}
            className="rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600"
          >
            Submit Another Enquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-14 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">

        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_340px]">

          {/* ── Left: form ── */}
          <div>
            <h1 className="text-2xl font-extrabold uppercase tracking-tight text-black sm:text-3xl">
              Request a Quote
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Fill out the form below and our team will get back to you within{" "}
              <strong className="font-semibold text-gray-800">12 hours</strong>.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              {/* Name row */}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="First Name" required error={errors.firstName}>
                  <input type="text" name="firstName" value={form.firstName} onChange={handleChange}
                    placeholder="John" className={inputClass(errors.firstName)} />
                </Field>
                <Field label="Last Name" required error={errors.lastName}>
                  <input type="text" name="lastName" value={form.lastName} onChange={handleChange}
                    placeholder="Smith" className={inputClass(errors.lastName)} />
                </Field>
              </div>

              {/* Contact row */}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Email Address" required error={errors.email}>
                  <input type="email" name="email" value={form.email} onChange={handleChange}
                    placeholder="john@company.com.au" className={inputClass(errors.email)} />
                </Field>
                <Field label="Phone Number" required error={errors.phone}>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                    placeholder="04XX XXX XXX" className={inputClass(errors.phone)} />
                </Field>
              </div>

              {/* Company */}
              <Field label="Company Name">
                <input type="text" name="company" value={form.company} onChange={handleChange}
                  placeholder="Your company (optional)" className={inputClass(false)} />
              </Field>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Region" required error={errors.region}>
                  <RegionSelect
                    value={form.region}
                    error={errors.region}
                    onChange={(region) => {
                      setForm((p) => ({ ...p, region }));
                      setErrors((p) => ({ ...p, region: "" }));
                    }}
                  />
                </Field>
                <Field label="Service Required" required error={errors.service}>
                  <select name="service" value={form.service} onChange={handleChange}
                    className={inputClass(errors.service)}>
                    <option value="">Select a service...</option>
                    {services.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>

              {/* Details */}
              <Field label="Project Details" required error={errors.details}>
                <textarea name="details" value={form.details} onChange={handleChange} rows={5}
                  placeholder="Please provide details about your project including location, dates, and any specific requirements..."
                  className={inputClass(errors.details)} />
              </Field>

              {/* Date */}
              <Field label="Preferred Start Date">
                <input type="date" name="startDate" value={form.startDate} onChange={handleChange}
                  className={inputClass(false)} />
              </Field>

              {submitError && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {submitError}
                </p>
              )}

              <button type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-orange-500/25 transition hover:bg-orange-600 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                {submitting ? "Sending…" : "Submit Enquiry"}
              </button>
            </form>
          </div>

          {/* ── Right: info sidebar ── */}
          <div className="flex flex-col gap-5">

            {/* Contact info card */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="mb-5 text-base font-bold text-black">Contact Information</h2>
              <div className="space-y-5">
                <InfoRow title="Phone" icon={
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>}>
                  <a href="tel:1300118170" className="hover:text-orange-500">1300 118 170</a>
                </InfoRow>
                <InfoRow title="Email" icon={
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>}>
                  <a href="mailto:info@focustrafficplans.com.au" className="break-all hover:text-orange-500">
                    info@focustrafficplans.com.au
                  </a>
                </InfoRow>
                <InfoRow title="Address" icon={
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>}>
                  Suite 4.04, 5 Celebration Drive<br />Bella Vista NSW 2153
                </InfoRow>
                <InfoRow title="Business Hours" icon={
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>}>
                  Monday – Friday: 7am – 5pm<br />24/7 Emergency Support
                </InfoRow>
              </div>
            </div>

            {/* Urgent card */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg shadow-orange-500/30">
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="mb-1.5 font-bold text-black">Need Urgent Assistance?</h3>
              <p className="mb-4 text-sm text-gray-500">
                For time-sensitive projects or emergency traffic management needs, call us directly.
              </p>
              <a href="tel:1300118170"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-orange-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call 1300 118 170
              </a>
            </div>

            {/* Service areas card */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="mb-1.5 font-bold text-black">Service Areas</h3>
              <p className="mb-4 text-sm text-gray-500">
                We provide traffic plans services nationwide, across all Australian states and territories.
              </p>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-2">
                {[
                  "New South Wales",
                  "Victoria",
                  "Queensland",
                  "Western Australia",
                  "South Australia",
                  "Tasmania",
                  "Northern Territory",
                  "ACT",
                ].map((area) => (
                  <li key={area} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
                    {area}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
